import type {
  DashboardData,
  ApiError,
  ImportRowIngreso,
  ImportRowGasto,
  ImportResult,
  IncomeRow,
  ExpenseRow,
  ExpensePatch,
  Importacion,
  Categoria,
  CategoriaTipo,
  FacturaMatchResult,
  ComisionRow,
  ComisionPatch,
  ComisionesSyncResult,
} from "./types";
import { authFetch } from "./auth/AuthContext";

if (!import.meta.env.VITE_API_URL) {
  console.warn("VITE_API_URL no está configurada.");
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function getJson<T>(path: string, search: Record<string, string> = {}): Promise<T> {
  const qs = new URLSearchParams(search).toString();
  const url = qs ? `${path}?${qs}` : path;
  const r = await authFetch(url);
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as Partial<ApiError>;
    throw new Error(err.detalle ?? err.error ?? `HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const r = await authFetch(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as Partial<ApiError>;
    throw new Error(err.detalle ?? err.error ?? `HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

async function deleteJson<T>(path: string): Promise<T> {
  const r = await authFetch(path, { method: "DELETE" });
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as Partial<ApiError>;
    throw new Error(err.detalle ?? err.error ?? `HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

async function patchJson<T>(path: string, body: unknown): Promise<T> {
  const r = await authFetch(path, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as Partial<ApiError>;
    throw new Error(err.detalle ?? err.error ?? `HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

// ─── Endpoints ───────────────────────────────────────────────────────────────

export function fetchDashboard(year: number, month: number, months = 12): Promise<DashboardData> {
  return getJson<DashboardData>("/api/dashboard", {
    year: String(year),
    month: String(month),
    months: String(months),
  });
}

export async function fetchIngresos(year: number, month: number): Promise<IncomeRow[]> {
  const data = await getJson<{ rows: IncomeRow[] }>("/api/ingresos", {
    year: String(year),
    month: String(month),
  });
  return data.rows;
}

export async function fetchGastos(year: number, month: number): Promise<ExpenseRow[]> {
  const data = await getJson<{ rows: ExpenseRow[] }>("/api/gastos", {
    year: String(year),
    month: String(month),
  });
  return data.rows;
}

export async function fetchImportaciones(tipo?: "ingreso" | "gasto"): Promise<Importacion[]> {
  const data = await getJson<{ rows: Importacion[] }>("/api/importaciones", tipo ? { tipo } : {});
  return data.rows;
}

export function importIngresos(
  rows: ImportRowIngreso[],
  archivo?: string,
): Promise<ImportResult> {
  return postJson<ImportResult>("/api/ingresos/import", { rows, archivo });
}

export function importGastos(
  rows: ImportRowGasto[],
  archivo?: string,
): Promise<ImportResult> {
  return postJson<ImportResult>("/api/gastos/import", { rows, archivo });
}

export function undoImportacion(id: string): Promise<{ tipo: string; deleted: number }> {
  return deleteJson<{ tipo: string; deleted: number }>(`/api/importaciones/${id}`);
}

// ─── Sugerencias de categoría (histórico para auto-categorizar) ────────────

export interface CategorySuggestion {
  proveedor: string;
  categoria: string;
  count: number;
}

export async function fetchGastoCategorySuggestions(): Promise<CategorySuggestion[]> {
  const data = await getJson<{ items: CategorySuggestion[] }>("/api/gastos/sugerencias-categoria");
  return data.items;
}

// ─── Edición de gastos ──────────────────────────────────────────────────────

export function updateGasto(id: string, patch: ExpensePatch): Promise<{ row: ExpenseRow }> {
  return patchJson<{ row: ExpenseRow }>(`/api/gastos/${id}`, patch);
}

export function deleteGasto(id: string): Promise<{ ok: true }> {
  return deleteJson<{ ok: true }>(`/api/gastos/${id}`);
}

export function bulkUpdateGastos(
  ids: string[],
  patch: ExpensePatch,
): Promise<{ updated: number }> {
  return patchJson<{ updated: number }>(`/api/gastos/bulk`, { ids, patch });
}

/** Sube una factura (PDF / imagen) y la asocia al gasto. */
export async function uploadFactura(id: string, file: File): Promise<{ filename: string }> {
  const fd = new FormData();
  fd.append("file", file);
  const r = await authFetch(`/api/gastos/${id}/factura`, { method: "POST", body: fd });
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as Partial<ApiError>;
    throw new Error(err.detalle ?? err.error ?? `HTTP ${r.status}`);
  }
  return (await r.json()) as { filename: string };
}

export function deleteFactura(id: string): Promise<{ ok: true }> {
  return deleteJson<{ ok: true }>(`/api/gastos/${id}/factura`);
}

/**
 * Sube un PDF/imagen de factura, lo lee con IA y devuelve los gastos
 * candidatos cuyo importe coincide. No vincula nada todavía: con el gasto
 * elegido se llama después a uploadFactura().
 */
export async function matchFactura(file: File): Promise<FacturaMatchResult> {
  const fd = new FormData();
  fd.append("file", file);
  const r = await authFetch("/api/facturas/match", { method: "POST", body: fd });
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as Partial<ApiError>;
    throw new Error(err.detalle ?? err.error ?? `HTTP ${r.status}`);
  }
  return (await r.json()) as FacturaMatchResult;
}

/** Descarga la factura como Blob y devuelve un object URL para abrirla. */
export async function openFacturaInNewTab(id: string): Promise<void> {
  const r = await authFetch(`/api/gastos/${id}/factura`);
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as Partial<ApiError>;
    throw new Error(err.detalle ?? err.error ?? `HTTP ${r.status}`);
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener");
  // Liberamos el blob URL tras un rato para no acumular memoria.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  if (!win) throw new Error("El navegador bloqueó la apertura de la ventana.");
}

// ─── Categorías (CRUD) ──────────────────────────────────────────────────────

export async function fetchCategorias(tipo?: CategoriaTipo): Promise<Categoria[]> {
  const data = await getJson<{ rows: Categoria[] }>(
    "/api/categorias",
    tipo ? { tipo } : {},
  );
  return data.rows;
}

export async function createCategoria(
  nombre: string,
  tipo: CategoriaTipo,
): Promise<Categoria> {
  const data = await postJson<{ row: Categoria }>("/api/categorias", { nombre, tipo });
  return data.row;
}

export async function renameCategoria(id: string, nombre: string): Promise<Categoria> {
  const data = await patchJson<{ row: Categoria }>(`/api/categorias/${id}`, { nombre });
  return data.row;
}

export function deleteCategoria(id: string): Promise<{ ok: true }> {
  return deleteJson<{ ok: true }>(`/api/categorias/${id}`);
}

export interface NotionSyncResult {
  importacion_id: string;
  inserted: number;
  skipped_duplicates: number;
  errors: number;
}

export function syncNotionIngresos(): Promise<NotionSyncResult> {
  return postJson<NotionSyncResult>("/api/notion-sync", {});
}

// ─── Comisiones comercial ────────────────────────────────────────────────────

export async function fetchComisiones(): Promise<ComisionRow[]> {
  const data = await getJson<{ rows: ComisionRow[] }>("/api/comisiones");
  return data.rows;
}

export function syncComisiones(): Promise<ComisionesSyncResult> {
  return postJson<ComisionesSyncResult>("/api/comisiones/sync", {});
}

export async function updateComision(id: string, patch: ComisionPatch): Promise<ComisionRow> {
  const data = await patchJson<{ row: ComisionRow }>(`/api/comisiones/${id}`, patch);
  return data.row;
}
